import { useEffect, useState } from 'react';
import { ArrowLeft, Calendar, Mail, Search, Users } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import {
  instructorService,
  type CourseStudent,
  type InstructorCourse,
} from '../services/instructorService';
import { formatEnglishDate } from '../lib/dateFormat';

const formatDate = (dateString: string | null) => {
  if (!dateString) {
    return 'No submissions';
  }

  return formatEnglishDate(dateString, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const CourseStudents = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<InstructorCourse | null>(null);
  const [students, setStudents] = useState<CourseStudent[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<CourseStudent[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchStudents = async () => {
      if (!courseId) {
        setError('Course not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const [coursesData, studentsData] = await Promise.all([
          instructorService.getCourses(),
          instructorService.getCourseStudents(courseId),
        ]);
        const currentCourse =
          coursesData.find((courseItem) => courseItem.id === courseId) ?? null;

        setCourse(currentCourse);
        setStudents(studentsData);
        setFilteredStudents(studentsData);
        setSelectedStudentId((current) =>
          studentsData.some((student) => student.id === current)
            ? current
            : studentsData[0]?.id ?? null
        );
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Failed to load students');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [courseId]);

  useEffect(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const nextFiltered = students.filter((student) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        student.display_name.toLowerCase().includes(normalizedSearch) ||
        student.email.toLowerCase().includes(normalizedSearch) ||
        student.student_id.toLowerCase().includes(normalizedSearch) ||
        student.department.toLowerCase().includes(normalizedSearch)
      );
    });

    setFilteredStudents(nextFiltered);
    setSelectedStudentId((current) =>
      nextFiltered.some((student) => student.id === current)
        ? current
        : nextFiltered[0]?.id ?? null
    );
  }, [searchQuery, students]);

  const selectedStudent =
    filteredStudents.find((student) => student.id === selectedStudentId) ??
    filteredStudents[0] ??
    null;
  const totalSubmissions = students.reduce(
    (sum, student) => sum + student.submission_count,
    0
  );
  const activeStudents = students.filter(
    (student) => student.submission_count > 0
  ).length;

  if (loading) {
    return (
      <div className="instructor-page">
        <InstructorLoadingSkeleton
          message="Loading course students..."
          variant="courses"
        />
      </div>
    );
  }

  return (
    <div className="instructor-page">
      <div className="instructor-top-bar">
        <div>
          <button
            className="instructor-secondary-button mb-3"
            onClick={() => navigate('/instructor/courses')}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            Back to Courses
          </button>
          <h1 className="instructor-page-title">Course Students</h1>
          <div className="instructor-page-sub">
            {course ? `${course.code} - ${course.title}` : 'Course roster'}
          </div>
        </div>
      </div>

      {error && <div className="instructor-error-state">{error}</div>}

      <div className="instructor-stat-row">
        <div className="instructor-stat-card navy">
          <div className="instructor-stat-label">Total Students</div>
          <div className="instructor-stat-value">{students.length}</div>
          <div className="instructor-stat-sub">Enrolled in this course</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Active Students</div>
          <div className="instructor-stat-value">{activeStudents}</div>
          <div className="instructor-stat-sub">With submissions</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Submissions</div>
          <div className="instructor-stat-value">{totalSubmissions}</div>
          <div className="instructor-stat-sub">Total received</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Average</div>
          <div className="instructor-stat-value small">
            {students.length > 0
              ? (totalSubmissions / students.length).toFixed(1)
              : '0.0'}
          </div>
          <div className="instructor-stat-sub">Submissions per student</div>
        </div>
      </div>

      <div className="instructor-split-grid">
        <aside className="instructor-list-panel">
          <div className="instructor-list-header">
            <div className="instructor-list-title-row">
              <div className="instructor-list-title">
                <span>Roster</span>
                <span className="instructor-list-count">{filteredStudents.length}</span>
              </div>
            </div>
            <div className="instructor-search-wrap">
              <Search strokeWidth={2} />
              <input
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by name, email, ID..."
                type="search"
                value={searchQuery}
              />
            </div>
          </div>

          <div className="instructor-list-scroll">
            {filteredStudents.length === 0 ? (
              <div className="instructor-empty-state">No students found.</div>
            ) : (
              filteredStudents.map((student) => (
                <button
                  className={
                    student.id === selectedStudent?.id
                      ? 'instructor-list-item active text-left'
                      : 'instructor-list-item text-left'
                  }
                  key={student.id}
                  onClick={() => setSelectedStudentId(student.id)}
                  type="button"
                >
                  <div className="instructor-list-item-top">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--instructor-navy)] text-xs font-bold text-white">
                        {getInitials(student.display_name)}
                      </span>
                      <span className="instructor-list-item-title">
                        {student.display_name}
                      </span>
                    </div>
                    <span className="instructor-badge">{student.submission_count}</span>
                  </div>
                  <div className="instructor-list-item-sub">{student.email}</div>
                  <div className="instructor-list-item-meta">
                    <span>{student.student_id}</span>
                    <span>{formatDate(student.last_submission_at)}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <section className="instructor-detail-panel">
          {selectedStudent ? (
            <>
              <div className="instructor-detail-header">
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="instructor-badge">{selectedStudent.student_id}</span>
                    <span className="instructor-status-badge instructor-status-submitted">
                      {selectedStudent.submission_count} submissions
                    </span>
                  </div>
                  <h2 className="instructor-detail-title">
                    {selectedStudent.display_name}
                  </h2>
                  <div className="instructor-detail-sub">
                    {selectedStudent.department || 'No department listed'}
                  </div>
                </div>
              </div>

              <div className="instructor-detail-body">
                <div className="instructor-meta-grid">
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Email</div>
                    <div className="instructor-meta-value break-words">
                      {selectedStudent.email}
                    </div>
                  </div>
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Enrolled</div>
                    <div className="instructor-meta-value">
                      {formatDate(selectedStudent.enrolled_at)}
                    </div>
                  </div>
                  <div className="instructor-meta-card">
                    <div className="instructor-meta-label">Last Submission</div>
                    <div className="instructor-meta-value">
                      {formatDate(selectedStudent.last_submission_at)}
                    </div>
                  </div>
                </div>

                <div className="instructor-feedback-list">
                  <div className="instructor-feedback-item">
                    <Mail strokeWidth={2.2} />
                    <div>
                      <div className="instructor-note-title">Contact</div>
                      <div className="instructor-note-text">{selectedStudent.email}</div>
                    </div>
                  </div>
                  <div className="instructor-feedback-item">
                    <Calendar strokeWidth={2.2} />
                    <div>
                      <div className="instructor-note-title">Submission Activity</div>
                      <div className="instructor-note-text">
                        {selectedStudent.submission_count > 0
                          ? `Last submitted ${formatDate(selectedStudent.last_submission_at)}.`
                          : 'No submissions received yet.'}
                      </div>
                    </div>
                  </div>
                  <div className="instructor-feedback-item">
                    <Users strokeWidth={2.2} />
                    <div>
                      <div className="instructor-note-title">Course Context</div>
                      <div className="instructor-note-text">
                        {course ? `${course.code} - ${course.title}` : 'Course roster'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="instructor-empty-state">Select a student to view details.</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CourseStudents;
